import type { PmsReportFormat, PmsReportId, PmsReportLanguage } from '@felix/domain';
import type { PdfOutput } from '../personnel/pdf.js';
import type { PersonnelVessel } from '../personnel/types.js';
import type { AuditTrailData, AuditTrailFilter } from './audit-trail.js';
import type {
  CmPreview,
  DefectInput,
  DefectList,
  DefectPatch,
  DefectView,
  Measurement,
  MeasurementEntry,
  MeasurementPoint,
  MeasurementPointInput,
  PmsAlertsView,
  PmsSettings,
  PointList,
} from './condition-types.js';
import type { PmsReportView } from './report-document.js';
import type { ReportPrintInfo } from './report-print.js';
import type {
  Assignee,
  ChecklistAnswer,
  CounterSuggestion,
  DecisionInput,
  EquipmentDetail,
  EquipmentInput,
  EquipmentTree,
  HistoryEntry,
  HistoryFilter,
  InstructionOption,
  IntervalChangeInput,
  IntervalHistoryEntry,
  JobChangeEntry,
  JobInput,
  JobList,
  JobView,
  OpenWorkOrderInput,
  PendingItem,
  PlanView,
  PmsAttachment,
  PostponementActionInput,
  PostponementInput,
  PostponementView,
  QueuedSetupChange,
  Reading,
  ReadingInput,
  RotableEditor,
  RotableList,
  SetupChangeListView,
  SetupChangeQuery,
  SparePartOption,
  VesselParticulars,
  WorkOrderList,
  WorkOrderView,
  WorkRecordForm,
} from './types.js';

/**
 * Ekipman ana verisini yazma (M7a): gemide etkinleşmiş geminin, ofiste kurulum devrindeki
 * geminin ağacı (K1). Hangi düğmenin görüneceğini ağacın `can` alanı söyler.
 */
export interface EquipmentEditor {
  /** Ofiste kurulumdaki etkin gemide değişiklik gemiye gider: `QueuedSetupChange` (ADR 0142). */
  create(
    vesselId: string,
    input: Partial<EquipmentInput> & { parentId: string | null },
  ): Promise<EquipmentDetail | QueuedSetupChange>;
  update(
    id: string,
    patch: { rowVersion: number; parentId?: string | null } & Partial<EquipmentInput>,
  ): Promise<EquipmentDetail | QueuedSetupChange>;
  decommission(
    id: string,
    rowVersion: number,
    reason: string,
  ): Promise<EquipmentDetail | QueuedSetupChange>;
  /**
   * Varsayılan iskelet (PMS belgesi §1, §17; K22): yalnız ekipmansız gemide ve kişinin adına.
   * Gemide ilk çekme bitmeden `INITIAL_PULL_PENDING`.
   */
  loadDefaultStructure?(vesselId: string): Promise<{ created: number }>;
}

/** Sayaç girişi: yalnız gemide (K13). Onay eksikse hata `{ code, detail }` taşır. */
export interface CounterEditor {
  addReading(
    equipmentId: string,
    input: ReadingInput,
  ): Promise<{ reading: Reading; equipment: EquipmentDetail }>;
  /** Noon Report önerisi (K2): yalnız değer, yazmaz. */
  suggestion(equipmentId: string): Promise<CounterSuggestion | null>;
}

/**
 * İş tanımı yazma (M7b3; K1): gemide; ofiste yalnız kurulum devrindeki gemide. Aralık kayıt
 * yokken gerekçeyle doğrudan düzeltilir, kayıttan sonra yalnız taleple değişir (K11).
 */
export interface JobEditor {
  /** Ofiste kurulumdaki etkin gemide değişiklik gemiye gider: `QueuedSetupChange` (ADR 0142). */
  create(
    vesselId: string,
    equipmentId: string,
    input: JobInput,
  ): Promise<JobView | QueuedSetupChange>;
  update(
    id: string,
    rowVersion: number,
    patch: Partial<JobInput> & { changeReason?: string | null; active?: boolean },
  ): Promise<JobView | QueuedSetupChange>;
  remove(id: string, rowVersion: number, reason: string): Promise<JobView | QueuedSetupChange>;
}

/**
 * Gemideki bakım kayıtları (M7b3; K13): iş emri, Complete & Report, onay, iptal ve düzeltme,
 * erteleme, aralık değişikliği. İmza niteliğindeki her işlem parolayı yeniden ister (§7).
 */
export interface PmsWorkEditor {
  openWorkOrder(input: OpenWorkOrderInput): Promise<WorkOrderView>;
  generate(horizonDays: number): Promise<{ created: WorkOrderView[] }>;
  start(id: string, rowVersion: number): Promise<WorkOrderView>;
  safetyCheck(
    id: string,
    rowVersion: number,
    items: { index: number; checked: boolean }[],
  ): Promise<WorkOrderView>;
  cancel(id: string, rowVersion: number, reason: string): Promise<WorkOrderView>;
  report(
    id: string,
    input: {
      /** Yalnız girildiyse (ADR 0075); verilmezse gövdede alan olmaz. */
      password?: string;
      safetyCheck?: { items: { index: number; checked: boolean }[] };
      record: WorkRecordForm;
      /** Measurements adımı (K19): raporla aynı transaction'da yazılır; FAIL arıza açar. */
      measurements?: MeasurementEntry[];
    },
  ): Promise<{ workOrder: WorkOrderView }>;
  decideRecord(recordId: string, input: DecisionInput): Promise<unknown>;
  voidRecord(recordId: string, reason: string, password?: string): Promise<unknown>;
  correctRecord(
    recordId: string,
    input: { reason: string; record: WorkRecordForm; password?: string },
  ): Promise<unknown>;
  postpone(input: PostponementInput): Promise<unknown>;
  decidePostponement(id: string, input: DecisionInput): Promise<unknown>;
  requestIntervalChange(jobId: string, input: IntervalChangeInput): Promise<unknown>;
  /**
   * Fotoğraf ya da PDF (K15): dosya küçültülüp gemi deposuna yazılır. `work_record`: beyan edilen
   * CM raporu sonradan kayda eklenir (ADR 0029 Ek M7f F4).
   */
  uploadAttachment?(
    ownerId: string,
    file: File,
    ownerType?: 'work_order' | 'work_record',
  ): Promise<PmsAttachment>;
  /** Kullanılan parçaların seçeneği: geminin stok kalemleri (M12). */
  spareParts?(vesselId: string): Promise<SparePartOption[]>;
  /** İş emrinin atanacağı gemi personeli. */
  assignees?(): Promise<Assignee[]>;
}

/** Ofis adımı (ADR 0026 §3): süperintendent erteleme ve aralık uzatması kararı (K10, K11). */
export interface PmsOfficeDecider {
  decidePostponement(id: string, input: DecisionInput): Promise<unknown>;
  decideIntervalChange(id: string, input: DecisionInput): Promise<unknown>;
  /**
   * Etkin gemide aralığı ofis girer (ADR 0104): karar gemiye gönderilir, gemi sunucusu uygular.
   * Ölçüm noktaları gemide kalır.
   */
  sendIntervalChange?(jobId: string, input: IntervalChangeInput): Promise<unknown>;
  /** Breakdown kapanışının süperintendent adımı (K24); parolayla. */
  decideDefect?(id: string, input: DecisionInput): Promise<unknown>;
  /**
   * Ertelemeye ofis notu ya da kararı (ADR 0108 §3): yorum gemide görünür; geri çevirme ve yeni
   * vade gemiye iner, gemi sunucusu uygular.
   */
  postponementAction?(id: string, input: PostponementActionInput): Promise<unknown>;
}

/**
 * Ölçüm noktası ve limit yazma (M7c; K1, K20): gemide etkinleşmiş geminin, ofiste kurulum
 * devrindeki geminin noktaları. Limit, kaynak ya da baseline değişikliği gerekçe ister [UR Z27
 * 2.2.4]; düğmeleri nokta listesinin `can` alanı belirler.
 */
export interface MeasurementPointEditor {
  create(vesselId: string, input: MeasurementPointInput): Promise<MeasurementPoint>;
  update(
    id: string,
    rowVersion: number,
    patch: Partial<MeasurementPointInput>,
  ): Promise<MeasurementPoint>;
  remove(id: string, rowVersion: number, reason: string): Promise<unknown>;
}

/**
 * CM içe aktarma (K20) [NR496 4.4.2d]: önizleme hiçbir şey yazmaz; uygulama aynı dosyayla tek
 * transaction'dır. Gemide eşik ve değer, ofiste (kurulum devri) yalnız eşik.
 */
export interface CmImporter {
  preview(vesselId: string, file: File): Promise<CmPreview>;
}

/**
 * Arıza ve breakdown (M7c; K24, K27): yalnız gemide açılır, düzeltilir ve kapanışa gönderilir
 * (K13). Kapanışa gönderim ve karar açık onay adımıdır; parola yalnız şirket ayarı açıkken (ADR 0075).
 */
export interface DefectEditor {
  create(input: DefectInput): Promise<{ defect: DefectView; workOrder: WorkOrderView | null }>;
  update(id: string, rowVersion: number, patch: DefectPatch): Promise<DefectView>;
  repairWorkOrder(id: string): Promise<{ defect: DefectView; workOrder: WorkOrderView }>;
  submit(id: string, password?: string): Promise<DefectView>;
  decide(id: string, input: DecisionInput): Promise<unknown>;
}

/** Kontrol listesi cevabı yeniden dışa açılır (rapor formu). */
export type { ChecklistAnswer };

/**
 * PMS ekranlarının veri kaynağı (M7a): web ofis ofis API'sine, gemi uygulaması IPC ile gemi
 * sunucusuna bağlar; ekran bir kez yazılır (ADR 0019 §2). Hata `{ code, detail? }` taşıyan
 * `Error`.
 */
export interface PmsSource {
  scope: 'web' | 'ship';
  vessels(): Promise<PersonnelVessel[]>;
  particulars(vesselId: string): Promise<VesselParticulars>;
  tree(vesselId: string): Promise<EquipmentTree>;
  equipment(id: string): Promise<EquipmentDetail>;
  readings(id: string): Promise<Reading[]>;
  editor?: EquipmentEditor;
  /** Yalnız gemide. */
  counter?: CounterEditor;
  /** Dolaşan bileşenler ve kendi saatleri (DNV-CP-0206 4.3 c; ADR 0133 §6). */
  rotables?(vesselId: string): Promise<RotableList>;
  /** Yalnız gemide (K13). */
  rotableEditor?: RotableEditor;
  // --- M7b3: görev kütüphanesi, plan, iş emirleri, geçmiş ve kararlar ---
  jobs?(vesselId: string): Promise<JobList>;
  plan?(vesselId: string, range?: { from?: string; to?: string }): Promise<PlanView>;
  workOrders?(vesselId: string): Promise<WorkOrderList>;
  workOrder?(id: string): Promise<WorkOrderView>;
  history?(vesselId: string, filter?: HistoryFilter): Promise<HistoryEntry[]>;
  /** İş emrinin ekleri; `work_record`: kaydın sonradan gelen CM raporu (F4). */
  attachments?(ownerId: string, ownerType?: 'work_order' | 'work_record'): Promise<PmsAttachment[]>;
  /** Ekin açılacağı adres (ofis); gemide yok. */
  attachmentUrl?(id: string): string;
  /** Kişinin karar vereceği bekleyen kayıtlar (gemide zil; ofiste ofis adımı). */
  pending?(): Promise<PendingItem[]>;
  /**
   * Ertelemeler (ADR 0108 §4): gemide kendi gemisi; ofiste `vesselId` verilmezse filo. Talep eden,
   * onaylayan, vadeler, süre, durum ve ofisin notları/kararları.
   */
  postponements?(vesselId: string | null): Promise<PostponementView[]>;
  /** Kurulum değişiklikleri ve gemideki sonuçları (ADR 0142 §3); yalnız ofiste. */
  setupChanges?(vesselId: string, query: SetupChangeQuery): Promise<SetupChangeListView>;
  /** İşin aralık geçmişi, kaynağıyla (Ship / Office; ADR 0104 §7). */
  intervalHistory?(jobId: string): Promise<IntervalHistoryEntry[]>;
  /** İşin aralık ve talimat değişiklikleri, kim, hangi rolle, ne zaman (DNV-CP-0206 4.6 b). */
  jobChangeLog?(jobId: string): Promise<JobChangeEntry[]>;
  /** Job Card talimat seçicisi: yalnız yayındaki PMS talimatları (K38). */
  instructions?(vesselId: string): Promise<InstructionOption[]>;
  jobEditor?: JobEditor;
  /** Yalnız gemide (K13). */
  work?: PmsWorkEditor;
  /** Yalnız ofiste. */
  office?: PmsOfficeDecider;
  /** Oturumdaki kişi (gemide): kendi kaydının iptali ve düzeltmesi (K14), uygulayıcı adı. */
  userId?: string;
  userName?: string;
  // --- M7c: ölçüm, CM, arıza, uyarılar ---
  measurementPoints?(vesselId: string, filter?: { equipmentId?: string }): Promise<PointList>;
  measurements?(
    vesselId: string,
    filter: { pointId?: string; workOrderId?: string },
  ): Promise<Measurement[]>;
  pointEditor?: MeasurementPointEditor;
  cm?: CmImporter;
  defects?(
    vesselId: string,
    filter?: { kind?: 'defect' | 'breakdown'; equipmentId?: string },
  ): Promise<DefectList>;
  defect?(id: string): Promise<DefectView>;
  /** Yalnız gemide (K13). */
  defectEditor?: DefectEditor;
  /** PMS uyarıları ve PMS dışı ekipman (K22, K25). */
  alerts?(vesselId: string): Promise<PmsAlertsView>;
  /** Settings ▸ PMS (K32): şirketin eşikleri; gemide salt okunur. */
  settings?(): Promise<PmsSettings>;
  /** Yalnız ofiste ("Ayarlar / düzenleme"). */
  settingsEditor?: {
    save(
      rowVersion: number | null,
      values: Partial<Omit<PmsSettings, 'rowVersion'>>,
    ): Promise<PmsSettings>;
  };
  // --- M7e: NR496 raporları (plan Task 28–29; History ▸ Reports Task 31) ---
  /** Raporun verisi (önizleme ve PDF; PDF istemcide üretilir, gemide internetsiz). */
  report?(vesselId: string, reportId: PmsReportId, query?: PmsReportFilter): Promise<PmsReportView>;
  /**
   * Raporun Excel ya da CSV dosyası (sunucuda üretilir): ofiste tarayıcı indirmesi, gemide ana
   * süreç kayıt penceresi. Vazgeçilirse sessizce döner.
   */
  exportReport?(
    vesselId: string,
    reportId: PmsReportId,
    options: { format: PmsReportFormat; lang: PmsReportLanguage; query?: PmsReportFilter },
  ): Promise<void>;
  /**
   * Denetim kaydı (plan M7 Task 30; `AuditTrailPanel`): gemide yalnız gemide yapılan
   * değişiklikler (K37), ofiste tam kaynak.
   */
  auditTrail?(vesselId: string, filter: AuditTrailFilter): Promise<AuditTrailData>;
  /**
   * Rapor PDF'inin basım bilgisi (Task 31) [NR496 4.1.2]: yazılım sürümü, basan kişi ve rolü,
   * şirket markası. PDF istemcide üretilir; gemide internetsiz.
   */
  reportPrint?(): Promise<ReportPrintInfo>;
  /** PDF'in kaydı: gemide kayıt penceresi; verilmezse tarayıcı indirmesi. */
  pdf?: PdfOutput;
}

/** Rapor süzgeci (`@felix/domain` `pmsReportQuerySchema`): gün aralığı, yıl, ekipman alt ağacı. */
export interface PmsReportFilter {
  from?: string;
  to?: string;
  year?: number;
  equipment?: string;
  /** Vessel Certificates: kategori (Task 27a). */
  category?: string;
  /** Klas ve kritik [NR496 4.4.2b, 4.5.3e, 4.6.3c]: `yes` ya da `no`. */
  class?: string;
  critical?: string;
  /** History [NR496 4.6.2b]: `planned` ya da `unplanned`; gecikmiş ve ertelenmiş raporlanan. */
  planning?: string;
  overdue?: string;
  postponed?: string;
}

/** Hatanın gösterilebilir ayrıntısı (ör. gereken onaylar `LOWER_THAN_PREVIOUS,…`). */
export function errorDetail(error: unknown): string | undefined {
  const detail = (error as { detail?: unknown } | null)?.detail;
  return typeof detail === 'string' ? detail : undefined;
}
